import { attributes } from '../../core/ids-attributes';

import { IdsConstructor } from '../../core/ids-element';
import { EventsMixinInterface } from '../../mixins/ids-events-mixin/ids-events-mixin';
import type IdsDataGrid from './ids-data-grid';
import IdsToolbar from '../ids-toolbar/ids-toolbar';
import IdsText from '../ids-text/ids-text';

type Constraints = IdsConstructor<EventsMixinInterface>;

/**
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsDataGridToolbarMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  /**
   * Reference to the toolbar element
   */
  #toolbar: HTMLElement | IdsToolbar | null = null;

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.CONTEXTUAL_TOOLBAR_ID,
    ];
  }

  connectedCallback() {
    super.connectedCallback?.();

    this.offEvent('rowselected', this);
    this.onEvent('rowselected', this, () => this.toggleToolbar());

    this.offEvent('rowdeselected', this);
    this.onEvent('rowdeselected', this, () => this.toggleToolbar());

    this.offEvent('rowremoved', this);
    this.onEvent('rowremoved', this, () => this.toggleToolbar());
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#toolbar = null;

    this.offEvent('rowselected', this);
    this.offEvent('rowdeselected', this);
    this.offEvent('rowremoved', this);
  }

  toggleToolbar() {
    const toolbar = this.toolbar as IdsToolbar;
    const selectedRows = this.dataGrid.selectedRowsAcrossPages;
    toolbar?.classList.toggle('active', selectedRows.length > 0);

    if (!this.toolbarSelectionCountText) return;

    const selectionCountText = this.toolbarSelectionCountText;
    let recordsSelectedText = this.dataGrid.localeAPI.translate('RecordsSelected');
    if (selectedRows.length === 1) {
      recordsSelectedText = this.dataGrid.localeAPI.translate('RecordSelected');
    }
    if (selectedRows.length > 0) {
      recordsSelectedText = recordsSelectedText.replace('{0}', selectedRows.length.toString());
      selectionCountText.textContent = recordsSelectedText;
    }
  }

  /**
   * Reference to the data grid parent
   * @returns {IdsDataGrid} the data grid parent
   */
  get dataGrid() {
    return this as unknown as IdsDataGrid;
  }

  /**
   * Set contextualToolbarId attribute
   * @param {string} value - The contextual toolbar id
   */
  set contextualToolbarId(value: string) {
    if (value) {
      this.setAttribute(attributes.CONTEXTUAL_TOOLBAR_ID, value);
      this.toolbar = document.querySelector<HTMLElement>(`#${value}`);
    } else {
      this.removeAttribute(attributes.CONTEXTUAL_TOOLBAR_ID);
    }
  }

  /**
   * Get contextualToolbarId attribute value
   * @returns {string} The contextual toolbar id
   */
  get contextualToolbarId(): string {
    return this.getAttribute(attributes.CONTEXTUAL_TOOLBAR_ID) || '';
  }

  /**
   * Set toolbar
   * @param {HTMLElement | IdsToolbar | null} value - A custom toolbar
   */
  set toolbar(value: HTMLElement | IdsToolbar | null) {
    this.#toolbar = value;
    this.#toolbar?.classList.add('contextual-toolbar', 'toolbar', 'ids-datagrid-contextual-toolbar');
  }

  /**
   * Get toolbar
   * @returns {HTMLElement | IdsToolbar | null} value - The custom toolbar
   */
  get toolbar() {
    return this.#toolbar;
  }

  /**
   * Get the selection count text element
   * @returns {IdsText | null} the selection count text element
   */
  get toolbarSelectionCountText() {
    return this.toolbar?.querySelector<IdsText>('.selection-count');
  }
};

export default IdsDataGridToolbarMixin;
